import CreateEntityButton from '@/shared/Buttons/CreateEntityButton'
import { useMemo, useState } from 'react'
import SearchInput from '@/shared/Inputs/SearchInput'
import usePrimitives from '@/pages/MDM/hooks/usePrimitives.ts'
import MDMListGroup from '@/pages/MDM/components/MDMListGroup'
import PrimitiveIcon from '@/assets/icons/mdm/enum-schema.svg?react'
import PrimitiveValueIcon from '@/assets/icons/mdm/enum-value.svg?react'
import CreatePrimitiveSidePage from '@/pages/MDM/components/Primitives/CreatePrimitiveSidePage'
import MDMListValue from '@/pages/MDM/components/MDMListValue'
import { IEnum } from '@/pages/MDM/mdm.types.ts'
import MenuOptionEdit from '@/shared/Dropdowns/DropdownListItem/DropdownListItemEdit'
import MenuOptionDelete from '@/shared/Dropdowns/DropdownListItem/DropdownListItemDelete'
import useSidePageWithTableCtrl from '@/shared/SidePage/useSidePageWithTableCtrl.ts'
import EditPrimitiveSidePage from '@/pages/MDM/components/Primitives/EditPrimitiveSidePage'
import confirmService from '@/services/ConfirmService/confirm.service.ts'
import mdmApi from '@/pages/MDM/mdm.api.ts'
import mdmEvents from '@/pages/MDM/events/mdm.events.ts'
import ProtectedContent from '@/services/rights/ProtectedContent'
import useSelfAccessRightsSet from '@/services/rights/useSelfAccessRIghtsSet.ts'
import useDebounce from '@/shared/hooks/useDebounce.ts'

const PrimitivesTabContent = () => {
  const selfAccessRights = useSelfAccessRightsSet()
  const primitives = usePrimitives()
  const sidePageCtrl = useSidePageWithTableCtrl(primitives)

  const [createSidePageOpen, setCreateSidePageOpen] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 500)

  const handleDeletePrimitive = (primitive: IEnum) => {
    confirmService.requestConfirm({
      description: `Подтвердите удаление примитива ${primitive.name}`,
      buttonType: 'primary',
      onConfirm: () =>
        mdmApi.deleteEnum(primitive.id).then((r) => r.status === 'success' && mdmEvents.enumDeleted(r.body.id)),
    })
  }

  const searchResults = useMemo(() => {
    if (!search) return primitives

    return primitives.filter(
      (p) =>
        p.name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        p.values.some((v) => v.name.toLowerCase().includes(debouncedSearch.toLowerCase()))
    )
  }, [primitives, debouncedSearch])

  const renderMenuOptions = (e: IEnum) => {
    if (!selfAccessRights.has('mdm:delete_schema') && !selfAccessRights.has('mdm:update_schema')) {
      return null
    }

    return (
      <div className="flex-lines">
        <ProtectedContent requiredRight="mdm:update_schema">
          <MenuOptionEdit text="Редактировать" onClick={() => sidePageCtrl.handleItemPageOpen(e)} />
        </ProtectedContent>
        <ProtectedContent requiredRight="mdm:delete_schema">
          <MenuOptionDelete text="Удалить" onClick={() => handleDeletePrimitive(e)} />
        </ProtectedContent>
      </div>
    )
  }

  return (
    <div className="flex-lines gap24" style={{ minHeight: 0 }}>
      <ProtectedContent
        requiredRight="mdm:create_schema"
        fallBack={
          <span className="text-nm text-description">
            Для создания нового примитива обратитесь к администратору системы
          </span>
        }
      >
        <CreateEntityButton onClick={() => setCreateSidePageOpen(true)} text="Создать примитив" />
      </ProtectedContent>
      <SearchInput value={search} name="search" onChange={setSearch} placeHolder="Поиск по примитивам" />
      <div className="flex-lines" style={{ overflow: 'auto', minHeight: 0 }}>
        {searchResults.map((p, index) => (
          <MDMListGroup
            renderSeparateLine={index !== primitives.length - 1}
            key={p.id}
            object={p as any}
            icon={<PrimitiveIcon />}
            menuOptions={renderMenuOptions(p)}
          >
            {p.values.map((v) => (
              <MDMListValue key={v.id} name={v.name} icon={<PrimitiveValueIcon />} />
            ))}
          </MDMListGroup>
        ))}
      </div>
      <CreatePrimitiveSidePage onClose={() => setCreateSidePageOpen(false)} open={createSidePageOpen} />
      <EditPrimitiveSidePage
        onCloseAnimationEnd={sidePageCtrl.handleCloseAnimationEnd}
        primitive={sidePageCtrl.selectedItem}
        open={sidePageCtrl.sidePageOpen}
        onClose={sidePageCtrl.handleClosePage}
      />
    </div>
  )
}

export default PrimitivesTabContent
